<script setup lang="ts">
import {useCoreAppArticlesFilter, useHelpArticlesAndVideos} from "~/utils/help";
import {faSearch} from "@fortawesome/pro-regular-svg-icons";
import {faPlay, faChevronRight, faLifeRing} from "@fortawesome/pro-light-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";

const store = useAppStore();
const query = ref<string>("");

const {core_apps = [], empty = false} = defineProps<{
    core_apps: any[]
    empty?: boolean
}>();

const {data: help, status: help_status} = useHelpArticlesAndVideos({
    query: query,
    apps: [useRoute().params.id as string],
    onAfterArticleFetch: (articles: any[]) => useCoreAppArticlesFilter(
        articles,
        (store.app?.name ?? useRoute().params.id ?? "") as string,
        core_apps as unknown as string[]
    ),
    debounce: 1000
});

function viewArticle(article: any) {
    document.querySelector(`#app_support button[tabindex]:nth-of-type(2)`)?.click();

    nextTick(() => {
        document.querySelector(`[data-article="${article.id}"]`)?.click();
    })
}
</script>

<template>
    <h3 v-if="empty" class="font-display font-medium">Getting started with {{ store.app.name }}</h3>
    <h3 v-else class="font-display font-medium">Guides, how to's, and answers to your questions about {{ store.app.name }}</h3>
    <AppEmptyStateApp v-if="empty" class="mb-2" />
    <UFormGroup class="mb-2">
        <UInput
            placeholder="Search videos and support articles..." size="xl"
            v-model="query"
        >
            <template #leading>
                <FontAwesomeIcon :icon="faSearch"/>
            </template>
        </UInput>
    </UFormGroup>
    <UIDataList
        ui="relative max-h-full overflow-y-auto bg-white border overflow-y-auto border-gray-300 rounded-md">
        <div class="p-4 flex flex-col gap-2">
            <HdLoader :status="help_status">
                <UTabs id="app_support" v-if="help.videos.length || help.articles.length" :items="[
                        {
                            slot: 'videos',
                            label: 'Videos',
                            keep: help.videos.length
                        },
                        {
                            slot: 'articles',
                            label: 'Support Articles',
                            keep: help.articles.length
                        }
                    ].filter(tab => tab.keep)">
                    <template #videos>
                        <div class="flex flex-col gap-2">
                            <UAccordion :items="help.videos.map((video, index) => ({
                            ...video,
                            defaultOpen: index === 0,
                        }))"
                                        class="flex flex-col gap-2"
                                        :ui="{
                container:
                    'flex rounded-md flex-col w-full bg-white ring-1 ring-inset ring-gray-300 shadow-sm',
                item: {
                    base: 'px-3 py-2',
                },
            }">
                                <template #default="{ item, open }">
                                    <UButton color="white" size="md" class="active:scale-100" variant="none">
                                        <template #leading>
                                            <FontAwesomeIcon :icon="faPlay" fixed-width/>
                                        </template>
                                        <template #trailing>
                                            <FontAwesomeIcon :icon="faChevronRight" fixed-width
                                                             class="transform duration-75 ease-in-out" :class="{
                                            'rotate-90': open
                                        }"/>
                                        </template>
                                        <span class="text-left flex-1">{{ item.title }}</span>
                                    </UButton>
                                </template>
                                <template #item="{ item }">
                                    <div class="px-3 -mt-1.5 py-3 border-t border-gray-300 -mx-3 flex flex-col">
                                        <div :key="item.id" v-html="item.html"
                                             class="mb-2 rounded-md shadow bg-hd-neutral-50 overflow-hidden [&>iframe]:max-w-full"/>
                                    </div>
                                    <p>{{ item.description }}</p>
                                    <UButton
                                        v-if="item.article"
                                        label="Need more info? Read full article"
                                        color="white"
                                        size="lg"
                                        class="mt-2"
                                        block
                                        @click="viewArticle(item.article)"
                                    >
                                        <template #leading>
                                            <FontAwesomeIcon
                                                :icon="faLifeRing"
                                                fixed-width
                                                class="text-sm transform duration-75 ease-in-out"
                                            />
                                        </template>
                                    </UButton>
                                </template>
                            </UAccordion>
                        </div>
                    </template>
                    <template #articles>
                        <UAccordion :items="help.articles.map((video, index) => ({
                            ...video,
                        }))"
                                    class="flex flex-col gap-2"
                                    :ui="{
                container:
                    'flex rounded-md flex-col w-full bg-white ring-1 ring-inset ring-gray-300 shadow-sm',
                item: {
                    base: 'px-3 py-2',
                },
            }">
                            <template #default="{ item, index,open,close }">
                                <UButton color="white" size="md" :data-article="open ? undefined : item.id" class="active:scale-100 flex-1" variant="none" :ui="{
                                    padding: {
                                        md: 'px-3 py-2'
                                    }
                                }">
                                    <template #trailing>
                                        <FontAwesomeIcon
                                            :icon="faChevronRight"
                                            fixed-width
                                            class="text-sm transform duration-75 ease-in-out"
                                            :class="{
                                                'rotate-90': open
                                            }"
                                        />
                                    </template>
                                    <template #leading>
                                        <FontAwesomeIcon
                                            :icon="faLifeRing"
                                            fixed-width
                                            class="text-sm transform duration-75 ease-in-out"
                                        />
                                    </template>
                                    <span class="text-left flex-1">{{ item.title }}</span>
                                </UButton>
                            </template>
                            <template #item="{ item }">
                                <div class="px-3 -mt-1.5 pt-3 border-t border-gray-300 -mx-3 flex" />
                                <div
                                    class="-mx-3 px-3 isolate [&_iframe]:w-full [&_iframe]:h-72 prose prose-sm prose-headings:font-display prose-headings:font-medium prose-hr:my-4 [&_details]:my-4 [&_img]:border [&_img]:rounded-md [&_img]:border-gray-300 [&_summary_b]:font-medium [&_summary_h2]:!m-0 [&_summary_h2]:!inline"
                                    v-html="item.body"
                                />
                            </template>
                        </UAccordion>
                    </template>
                </UTabs>
            </HdLoader>
        </div>
    </UIDataList>
</template>